import type { PortfolioEntry } from '@aithora/shared';
import { useConsoleRole } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { DevNote } from '../../ui/buildLabels';
import { useReadOnly } from '../../ui/ReadOnly';
import { tierLabel } from '../residents/common';
import type { BenchRow } from './portfolioHead';

/**
 * The V9 Portfolio roll-up (design portfolio.js, isPortfolio) at the top of Performance ›
 * Portfolio: the console keeps no separate Portfolio page, so the owner's roll-up sits where the
 * operator's buildings already are. One Aithora-wide set of counts from the same board every
 * row reads (GET /api/financial/facilities/benchmarks/all): the buildings in view, and the
 * residents Aithora has on file against their beds ("11 buildings · 891 of 1,152 beds
 * occupied"). The design's owner count ("· 1 owner") is not served by the board, so it is not
 * said. "Needs you this week" has no feed yet (BACKEND-GAPS: owner inbox), so it says so and
 * shows no numbers.
 */

/** A building is connected when Aithora has its residents on file. */
const connected = (b: Pick<PortfolioEntry, 'residents'>) => typeof b.residents === 'number' && b.residents > 0;
const num = (n: number) => Math.round(n).toLocaleString('en-US');

/**
 * "11 buildings · 891 of 1,152 beds occupied". The occupied count is said only when every
 * building is connected and has its beds on file; otherwise the residents counted would leave
 * out buildings Aithora cannot see yet, so it says how many are connected instead (the phone's
 * wording, owner.ts summaryLine).
 */
export function rollupLine(list: readonly Pick<PortfolioEntry, 'beds' | 'residents'>[]): string {
  if (!list.length) return 'No buildings yet';
  const b = `${list.length} ${list.length === 1 ? 'building' : 'buildings'}`;
  const allBeds = list.every((x) => typeof x.beds === 'number' && x.beds > 0);
  const beds = allBeds ? list.reduce((n, x) => n + (x.beds ?? 0), 0) : null;
  const on = list.filter(connected);
  if (on.length === list.length && beds !== null) return `${b} · ${num(on.reduce((n, x) => n + (x.residents ?? 0), 0))} of ${num(beds)} beds occupied`;
  return `${b}${beds !== null ? ` · ${num(beds)} beds` : ''} · ${on.length} connected`;
}

const CARD = 'flex:0 0 auto;min-width:0;border:1px solid rgba(34,39,46,.12);border-radius:10px;background:#FCFAF4;padding:14px 16px;display:flex;flex-direction:column;gap:2px;';
const LABEL = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;';

export function PortfolioRollup({ facilities }: { facilities: readonly PortfolioEntry[] }) {
  const { role } = useConsoleRole();
  const ro = useReadOnly() || role === 'PARTNER' || role === 'DEVELOPER';
  return (
    <div data-screen-label="Portfolio" data-testid="portfolio-rollup" style={sx('display:flex;flex-direction:column;gap:12px;min-width:0;margin:6px 0 4px;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:flex-end;justify-content:space-between;gap:12px;flex-wrap:wrap;')}>
        <div style={sx('min-width:0;')}>
          <div style={sx('font-size:22px;font-weight:800;letter-spacing:-.02em;color:#22272E;')}>Portfolio</div>
          <div data-testid="portfolio-rollup-line" style={sx('font-size:16px;color:#54606C;margin-top:3px;')}>
            {rollupLine(facilities)}
          </div>
        </div>
        {ro && (
          <span data-testid="portfolio-ro" style={sx('font-size:14px;color:#54606C;')}>
            {'Read-only as ' + tierLabel(role)}
          </span>
        )}
      </div>
      <div data-screen-label="Needs you" data-testid="portfolio-needs" style={sx(CARD)}>
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:4px;')}>
          <span style={sx(LABEL + 'color:#264C7E;')}>Needs you this week</span>
          <span style={sx('display:inline-block;white-space:nowrap;font-size:14px;font-weight:800;letter-spacing:.07em;color:#54606C;background:rgba(84,96,108,.12);border-radius:3px;padding:2px 5px;')}>NOT CONNECTED</span>
        </div>
        <span style={sx('font-size:16px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>What needs you each week shows here once it is connected. Nothing is filled in until then.</span>
        <DevNote>
          <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>Not served yet: the owner inbox (BACKEND-GAPS), and an owner on each building for the owner count.</span>
        </DevNote>
      </div>
    </div>
  );
}

/** The side-by-side rows for two buildings, from the board's own fields (design cmpRows). */
export function compareRows(a: BenchRow, b: BenchRow, entry: (id: string) => PortfolioEntry | undefined): { k: string; a: string; b: string }[] {
  const occ = (r: BenchRow) => {
    const e = entry(r.id);
    return e && typeof e.residents === 'number' && r.beds > 0 ? `${num(e.residents)} of ${num(r.beds)}` : 'not on file';
  };
  const occPct = (r: BenchRow) => {
    const e = entry(r.id);
    return e && typeof e.residents === 'number' && r.beds > 0 ? Math.round((e.residents / r.beds) * 100) + '%' : '–';
  };
  const chg = (d: number | null) => (d === null || d === 0 ? '–' : (d > 0 ? '+' : '−') + Math.abs(d) + (Math.abs(d) === 1 ? ' point' : ' points'));
  const leak = (v: number | null) => (v === null ? '–' : '$' + v.toLocaleString('en-US') + 'K');
  const up = (v: number | null) => (v == null ? '–' : '$' + Math.round(v / 1000).toLocaleString('en-US') + 'K');
  return [
    { k: 'Grade', a: a.grade, b: b.grade },
    { k: 'Composite', a: String(a.comp), b: String(b.comp) },
    { k: 'Change since last quarter', a: chg(a.delta), b: chg(b.delta) },
    { k: 'Occupied', a: occ(a), b: occ(b) },
    { k: 'Occupancy', a: occPct(a), b: occPct(b) },
    { k: 'Uncaptured reimbursement / yr', a: leak(a.leakYr), b: leak(b.leakYr) },
    { k: 'Upside per grade step / yr', a: up(a.upsideUsd), b: up(b.upsideUsd) },
  ];
}

/** One plain sentence under the comparison, from the two composites (design cmpNote). */
export function compareNote(a: BenchRow, b: BenchRow): string {
  if (a.comp === b.comp) return `${a.name} and ${b.name} have the same composite, ${a.comp}.`;
  const [hi, lo] = a.comp > b.comp ? [a, b] : [b, a];
  const d = hi.comp - lo.comp;
  return `${hi.name} scores ${hi.comp} against ${lo.comp} for ${lo.name}, ${d} ${d === 1 ? 'point' : 'points'} apart.`;
}

export function SideBySide({ a, b, entry }: { a: BenchRow; b: BenchRow; entry: (id: string) => PortfolioEntry | undefined }) {
  return (
    <div data-screen-label="Side by side" data-testid="portfolio-compare" style={sx(CARD + 'gap:6px;margin-top:12px;')}>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
        <span style={sx(LABEL)}>Side by side</span>
      </div>
      <div style={sx('display:grid;grid-template-columns:minmax(140px,1fr) minmax(0,1fr) minmax(0,1fr);gap:8px 14px;font-size:16px;')}>
        <span />
        <span style={sx('font-weight:800;color:#22272E;overflow-wrap:break-word;')}>{a.name}</span>
        <span style={sx('font-weight:800;color:#22272E;overflow-wrap:break-word;')}>{b.name}</span>
        {compareRows(a, b, entry).map((c) => (
          <span key={c.k} style={sx('display:contents;')}>
            <span style={sx('color:#54606C;')}>{c.k}</span>
            <span style={sx('font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;')}>{c.a}</span>
            <span style={sx('font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;')}>{c.b}</span>
          </span>
        ))}
      </div>
      <div style={sx('font-size:16px;color:#22272E;line-height:1.45;padding-top:6px;border-top:1px solid rgba(34,39,46,.08);')}>{compareNote(a, b)}</div>
    </div>
  );
}

/** The Compare chip on a row (design cmpStyle). */
export function compareChip(on: boolean): string {
  return (
    'display:inline-flex;align-items:center;justify-content:center;min-height:30px;padding:0 10px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;flex:0 0 auto;' +
    (on ? 'color:#FCFAF4;background:#2F5C97;border:1px solid #2F5C97;' : 'color:#264C7E;border:1px solid rgba(47,92,151,.4);')
  );
}
